import { isClientFault } from '@baex/shared';
import type { TransferLogEntry } from '@baex/shared';

/**
 * Fixed mapping from error code to the label the table shows (see the "Table" section).
 * One label per code, always — never a per-row judgement call.
 */

export type StatusLabel =
  'stored' | 'deleted' | 'listed' | 'replayed' | 'error (client)' | 'error (server)';

export function statusLabel(entry: TransferLogEntry): StatusLabel {
  if (entry.status !== 'error') return entry.status;
  return isClientFault(entry.code) ? 'error (client)' : 'error (server)';
}

/** Server-side failures get the red-tinted row so they stand out while scrolling. */
export function isServerFault(entry: TransferLogEntry): boolean {
  return statusLabel(entry) === 'error (server)';
}

export function isErrorEntry(entry: TransferLogEntry): boolean {
  return entry.status === 'error';
}

/** What the SIZE column shows for an entry that refers to no stored file (see the "Table" section). */
export const NO_VALUE = '—';

/**
 * The FILE column (see the "Table" section).
 *
 * `(upload)` is the spec's literal, and only for the case it describes: an upload that
 * failed before the file was ever named. Every other entry without a filename refers to
 * no file at all — a listing moved nothing — and gets the same em dash the SIZE column
 * uses for exactly that reason.
 *
 * It used to print `(${entry.op})`, which put the internal operation name into a
 * user-facing column: a client polling once a minute filled the log with rows reading
 * `(list)`, a word from the API surface that names no file and appears nowhere else in
 * this interface.
 */
export function displayFileName(entry: TransferLogEntry): string {
  if (entry.fileName && entry.fileName.length > 0) return entry.fileName;
  return entry.op === 'upload' ? '(upload)' : NO_VALUE;
}

/**
 * How an entry is named in a heading, where an em dash would read as "Detail — —".
 *
 * Follows the shape of the spec's own wireframe for a row with no filename ("Detail —
 * upload attempt at 02:00:15"): the operation and when it happened, which is the only
 * thing that identifies such an entry to a reader.
 */
export function describeEntry(entry: TransferLogEntry, when: string): string {
  if (entry.fileName && entry.fileName.length > 0) return entry.fileName;
  return `${entry.op} attempt at ${when}`;
}

export const ROOT_SUBFOLDER_LABEL = '(root)';

export function displaySubfolder(subfolder: string | null): string {
  return subfolder && subfolder.length > 0 ? subfolder : ROOT_SUBFOLDER_LABEL;
}

/** Detail shown for 4xx-style client mistakes, where there is nothing on our side to look at. */
export const NO_DETAIL_TEXT =
  '(none — this is a client request mistake, nothing to investigate on our side)';
